import { Trans } from '@lingui/macro'

import * as React from 'react'

import { useMOrderTipUpdate } from '~graphql/generated/clientGraphql'
import { useFormContext, useFormState, useWatch } from '~shared/segments/Form'
import { TOrderForm, useFormOrderCtx } from '~shared/segments/FormScreen'
import { InputControl } from '~ui/components/formElements/accessories/InputControl'
import { InputLabel } from '~ui/components/formElements/accessories/InputLabel'

import { isPostPaidMethod } from '../'
import { TipSelect } from './TipSelect'

const NO_TIP = 0
const TIP_VALUES = [NO_TIP, 5, 10, 15, 20]
const TIP_UNIT = '%'

const tipOptions = TIP_VALUES.map((value, index) => ({ id: index, value }))

export type TTipOption = (typeof tipOptions)[number]

// a list of Qs that should be re-fetched after updating the tip
const refetchQueries = [`QOrderForCart`]

type TProps = NoChildren

/**
 * Handles tip enabling, posting it to the BE
 * and rendering a TipSelect.
 * Interacts with paymentMethod form field as well to show/hide the tip select when it is or isn’t relevant.
 */
export const TipInput: React.FC<TProps> = () => {
  const formOrder = useFormOrderCtx()

  const { control } = useFormContext<TOrderForm>()
  const { isSubmitting } = useFormState({ control })
  const paymentMethod = useWatch({ name: `paymentMethod`, control })

  // we need to update the tip directly on BE to recalculate the Cart info
  // so the local state is synced directly to the server
  // no need to store the tip in the order form as well
  // (or it could be moved up there & reacted to the changes somewhere else – splitting the logic and the UI in this component)
  const [mUpdateTip] = useMOrderTipUpdate({ refetchQueries })
  const [selectedTip, setSelectedTip] = React.useState<TTipOption>(tipOptions[0])

  // update tip directly on BE whenever:
  //  - payment method changes (could change from pre-paid to post-paid)
  //  - tip input is rendered & customer changes the selected tip value
  React.useEffect(() => {
    const updateOrderTip = async () => {
      if (!formOrder?.id) {
        return
      }

      if (!paymentMethod) {
        return
      }

      // post-paid orders (eg. cash/card on handover) should not have any tip in our system
      // because those tips are handled by the staff/driver
      // -> we only need tips on pre-paid orders because those are paid online
      const tip = isPostPaidMethod(paymentMethod.paymentGate.enum) ? NO_TIP : selectedTip.value

      await mUpdateTip({
        variables: {
          orderId: formOrder.id,
          tip,
        },
      })
    }

    updateOrderTip()
  }, [formOrder?.id, mUpdateTip, paymentMethod, selectedTip.value])

  // as explained in the effect above, we do not store the tip value for post-paid orders
  // no need to render the select
  if (!paymentMethod || isPostPaidMethod(paymentMethod.paymentGate.enum)) {
    return null
  }

  return (
    <InputControl>
      <InputLabel>
        <Trans>Tip</Trans>
      </InputLabel>
      <TipSelect
        tipUnit={TIP_UNIT}
        tipOptions={tipOptions}
        onSelect={setSelectedTip}
        selectedTip={selectedTip}
        isDisabled={isSubmitting}
      />
    </InputControl>
  )
}
